import { Box, Center, Container, Flex, Heading, Image, Input, SimpleGrid, Span, Stack, Text, useToken } from '@chakra-ui/react'
import { Header } from './header'
import { Navbar } from './navbar'
import { Sidebar } from './sidebar'
import { useColorMode, useColorModeValue } from './ui/color-mode'
import { PromptButton } from './ui/prompt-button'
import { HiBookOpen, HiLightBulb, HiMap, HiMail, HiLockClosed } from 'react-icons/hi'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { Footer } from './footer'
import { useChatHistory } from './chat-history'
import { useColorPalette } from './ui/color-palette-context'
import { ProgressCircleRing, ProgressCircleRoot } from './ui/progress-circle'
import { useAccount } from './ui/account-context';
import { ChatbotSelect } from './ui/chatbot-select'
import { useChatbotContext } from './ui/chatbot-context'
import { sendMessage } from '@/api'
import { Chatbot } from './ui/chatbot'
import { useAuth } from './ui/auth-context'
import { Login } from './login'

/**
 * Props for the Block component.
 */
interface BlockProps {
    /** Function to update the global font family. */
    setFontFamily: Function,
    /** The current font family string. */
    fontFamily: string,
}

/**
 * Context type for managing the visibility of the hero/intro section.
 */
interface HeroContextType {
    /** Whether the hero section is currently visible. */
    heroVisible: boolean;
    /** Function to update the hero visibility. */
    setHeroVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Context for sharing hero visibility state across components.
 */
export const HeroContext = createContext<HeroContextType | undefined>(undefined);

/**
 * The main layout component of the application.
 * Manages the layout including Header, Sidebar, Chatbot, and Footer.
 * Handles the high-level logic for sending messages and switching between sessions.
 * 
 * @param {BlockProps} props - The component props.
 * @returns {JSX.Element} The rendered application layout.
 */
export const Block = (props: BlockProps) => {

    const [heroVisible, setHeroVisible] = useState(true);
    const heroContextValue = { heroVisible, setHeroVisible }
    const { accountType, setAccountType, allowedBots } = useAccount();
    const { user, loading } = useAuth();
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const boxRef = useRef<HTMLDivElement>(null);
    const textColor = useColorModeValue('var(--chakra-colors-color-palette-500)', 'var(--chakra-colors-color-palette-500)');
    const messengerBGcolor = useColorModeValue('var(--chakra-colors-bg)', 'var(--chakra-colors-bg-muted)');
    const inputBGcolor = useColorModeValue('var(--chakra-colors-bg-muted)', 'var(--chakra-colors-bg-emphasized)');
    const { colorPalette } = useColorPalette();
    const { colorMode } = useColorMode();
    //const [message, setMessage] = useState('');
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [chatResponse, setChatResponse] = useState<string | null>(null);
    const {
        currentSessionId,
        currentMessages,
        addMessage,
        loadSession,
        startNewSession,
        getSessionHistories,
        deleteSession,
        refreshConversations
    } = useChatHistory();

    const { setCurrentSessionId } = useAccount();

    const { selectedChatbotValue, setSelectedChatbotValue } = useChatbotContext();

    /**
     * Handles sending a message from the user to the chatbot.
     * Updates the local message list and calls the API.
     * 
     * @param {string} msgText - The text of the message to send.
     */
    const handleSendMessage = async (msgText: string) => {
        if (!msgText.trim()) {
            return; // Don't send empty messages
        }
        
        const timestamp = new Date().toISOString();
        addMessage({ type: 'user', text: msgText, timestamp });
        
        try {
            const agentId = allowedBots?.[selectedChatbotValue]?.agentId ?? 'null'
            setChatResponse('Sending...'); 
            const response = await sendMessage(msgText, currentSessionId, agentId);
            
            const botTimestamp = new Date().toISOString();
            addMessage({ type: 'bot', text: response.reply, timestamp: botTimestamp }); 
            
            if (response.sessionId && response.sessionId !== currentSessionId) {
                setCurrentSessionId(response.sessionId);
            }
            
            // Refresh conversation list in sidebar
            refreshConversations();
            
            setChatResponse(null); 
        } catch (error) {
            console.error('Failed to send message:', error);
            setChatResponse('Error: Could not send message.'); 
            addMessage({ type: 'bot', text: 'Error: Could not send message.', timestamp: new Date().toISOString() }); 
        }
    };

    /**
     * Starts a new, empty chat session.
     */
    const handleNewChat = () => {
        startNewSession();
    }

    /**
     * Loads an existing chat session and sets the appropriate chatbot agent.
     * 
     * @param {string} sessionId - The ID of the session to load.
     */
    const handleOldChatSelected = async (sessionId: string) => {
        await loadSession(sessionId);
        
        // Find the session in conversations to get its agentId
        const session = getSessionHistories().find(s => s.sessionId === sessionId);
        if (session?.agentId && allowedBots) {
            // Find the index of the bot with this agentId
            const botIndex = allowedBots.findIndex(bot => bot.agentId === session.agentId);
            if (botIndex !== -1) {
                setSelectedChatbotValue(botIndex);
            }
        }
    }

    const [inputValue, setInputValue] = useState('');
    
    /**
     * Populates the chat input when a suggested prompt is clicked.
     * 
     * @param {string} prompt - The text content of the prompt.
     */
    const handlePromptClick = useCallback((prompt: string) => {
        //This will auto-populate the input section with text from the prompt.
        setInputValue(prompt);
    }, []);

    /**
     * Helper to retrieve a shadow root from an element.
     * @param el - The element to check.
     * @returns The shadow root if present, otherwise null.
     */
    function getShadowRoot(el: HTMLElement | Element | any | null): ShadowRoot | null {
        if (!el) return null; // Guard against null element
        if (el.shadowRoot) {
            return el.shadowRoot;
        }
        return null;
    }

    // Effect to track the dimensions of the chat window for responsive layout
    useEffect(() => {
        const updateDimensions = () => {
            if (boxRef.current) {
                setDimensions({
                    width: boxRef.current.offsetWidth,
                    height: boxRef.current.offsetHeight,
                });
            }
        };

        updateDimensions(); // Initial update

        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    /**
     * Determines whether the hero prompts should be displayed based on the current state.
     * @returns {'flex' | 'none'} The display property for the prompts.
     */
    const handleHidePrompts = () => {

        if (heroVisible && currentMessages.length < 1) {
            return 'flex'
        }
        else if (currentMessages.length >= 1) {
            return 'none'
        }
        else if (!heroVisible) {
            return 'none'
        }
        else {
            console.log('Fallback prompt state...');
            console.log(heroVisible);
            console.log(currentMessages.length);
            return 'flex'
        }
    }

    // Define a mapping object for icons
    const iconMap: { [key: string]: any } = {
        HiLightBulb: <HiLightBulb />,
        HiMap: <HiMap />,
        HiBookOpen: <HiBookOpen />,
        HiMail: <HiMail />,
        HiLockClosed: <HiLockClosed />
    };

    if (loading) {
        return (
            <Center height="100vh">
                <ProgressCircleRoot value={null} size="xl">
                    <ProgressCircleRing cap="round" />
                </ProgressCircleRoot>
            </Center>
        );
    }

    if (!user) {
        return <Login fontFamily={props.fontFamily} />;
    }

    return (
        <Box height={'100vh'}>
            <Header fontFamily={props.fontFamily} />
            <Navbar hideFrom="md" />
            <Flex height={'calc(100% - 88px)'} flex="1">
                <HeroContext.Provider value={heroContextValue}>
                    <Sidebar
                        onNewSession={handleNewChat}
                        onOldChatSelected={handleOldChatSelected}
                        onDeleteChat={deleteSession}
                        chatSessions={getSessionHistories}
                        fontFamily={props.fontFamily}
                        setFontFamily={props.setFontFamily}
                        maxW="280px"
                        position="sticky"
                        top="0"
                        height={"100%"}
                        hideBelow="md"
                    />
                    <Stack gap={0} flex="1" alignItems="stretch" maxH='100%'>
                        <Container p={0} m={0} height={'100%'} width={'100%'}  >
                            <Box display={handleHidePrompts()} alignItems={'center'} backgroundColor={'transparent'} position={'absolute'} w={'calc(100vw - 280px)'} h={'85%'} zIndex={'999'} top={'0'}>
                                <ChatbotSelect position={'absolute'} top={0} fontFamily={props.fontFamily} zIndex={'1400'} />
                                <Container maxW="4xl" >
                                    <Stack gap="10">
                                        <Heading fontFamily={props.fontFamily} size="4xl" fontWeight="normal">
                                            <Text fontSize={'7xl'} fontWeight={'700'} textAlign={'left'} color={`${colorPalette}.solid`}>{allowedBots?.[selectedChatbotValue]?.title ?? 'null'}</Text>
                                            <Text pt={'1rem'} fontSize={'4xl'} textAlign={'left'} color="fg.muted/50">{allowedBots?.[selectedChatbotValue]?.subtitle ?? 'null'}</Text>
                                        </Heading>
                                        <SimpleGrid columns={{ base: 2, md: 4 }} gap="4">
                                            {allowedBots?.[selectedChatbotValue]?.prompts.map((prompt, index) => (
                                                <PromptButton
                                                    key={index}
                                                    headerText={prompt.title}
                                                    fontFamily={props.fontFamily}
                                                    onPromptClick={handlePromptClick}
                                                    icon={prompt.icon ? iconMap[prompt.icon] : <HiLightBulb />}
                                                >
                                                    {prompt.content}
                                                </PromptButton>
                                            )) ?? 'null'}
                                        </SimpleGrid>
                                    </Stack>
                                </Container>
                            </Box>
                            <Box ref={boxRef} backgroundColor={useColorModeValue('#ffffff', '#181818')} width={'calc(100vw - 280px)'} height={'100%'} display={'flex'}>
                                <Chatbot 
                                    messages={currentMessages} 
                                    onEnter={handleSendMessage} 
                                    width={dimensions.width} 
                                    height={dimensions.height}
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    fontFamily={props.fontFamily}
                                    isThinking={chatResponse === 'Sending...'} />
                            </Box>
                            {/* <Box width={'calc(100vw - 280px)'} height={'3%'} color={'fg.muted'} backgroundColor={useColorModeValue('#ffffff', '#181818')} fontSize={'xs'}>DISCLAIMER: Our AI models can make mistakes or produce inaccurate responses. Please verify results against references.</Box> */}
                            {/* <Content /> we arent using this - remove later */}
                        </Container>
                    </Stack>
                </HeroContext.Provider>
            </Flex>
            <Footer />
        </Box>
    )
}
